import {
  Divider,
  Input,
  makeStyles,
  Spinner,
} from "@fluentui/react-components";
import React, { useContext, useEffect, useState } from "react";
import { AmdAmber } from "../../../components-v2/common/icons/status/amber/Amber";
import { AmdGreen } from "../../../components-v2/common/icons/status/green/Green";
import { AmdRed } from "../../../components-v2/common/icons/status/red/red";
import { NoDataCard } from "../../../components-v2/common/no-data-card/NoDataCard";
import { NotificationContext } from "../../../context/NotificationContext";
import { useApiResource } from "../../../hooks/useApiResource";
import { IThresholdPayload } from "../../../models/admin/IDimension";
import { useAppSelector } from "../../../store/StoreHooks";
import { getAirportCode } from "../../../store/airport/AirportSelector";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
  MESSAGEBAR_TYPE,
} from "../../../utils/common/Constants";

const defaultThresholdValues = {
  amberToRed: 0,
  greenToAmber: 1,
};

const hopSettingsURL = process.env.REACT_APP_API_GET_HOP_SETTINGS;
const baseURL = process.env.REACT_APP_API_BASE_URL;
const url = `${baseURL}${hopSettingsURL}`;
const additionalDelayUrl = url.replace("{kpiType}", "turnaroundDelay");

export interface IAdditionalDelaySettingsProps {
  refreshData: boolean;
  setRefreshData: (value: boolean) => void;
  resetTrigger?: number;
}

const AdditionalDelaySettings: React.FC<IAdditionalDelaySettingsProps> = ({
  refreshData,
  setRefreshData,
  resetTrigger,
}) => {
  const classes = useStyles();
  const airportCode = useAppSelector((state) => getAirportCode(state));
  const { addNotification } = useContext(NotificationContext);

  const [thresholdValues, setThresholdValues] = useState(
    defaultThresholdValues
  );

  const apiUrl = additionalDelayUrl.replace(
    AIRPORT_CODE_PARAMETER,
    airportCode
  );

  const {
    data = [],
    isLoading,
    isError,
    update,
    isUpdating,
    isUpdateSuccess,
    isUpdateError,
  } = useApiResource<IThresholdPayload[]>({
    queryKey: ["turnaroundDelay", airportCode],
    url: apiUrl,
    method: "PUT",
  });

  useEffect(() => {
    if (data.length > 0) {
      const configValues = data[0];
      setThresholdValues({
        amberToRed: configValues.amberToRed,
        greenToAmber: configValues.greenToAmber,
      });
    }
  }, [data]);
  useEffect(() => {
    if (data.length > 0) {
      const configValues = data[0];
      setThresholdValues({
        amberToRed: configValues.amberToRed,
        greenToAmber: configValues.greenToAmber,
      });
    } else {
      setThresholdValues(defaultThresholdValues);
    }
  }, [resetTrigger]);
  useEffect(() => {
    if (isUpdateSuccess) {
      addNotification(
        MESSAGEBAR_TYPE.SUCCESS,
        `Additional Delay settings saved successfully`
      );
    } else if (isUpdateError) {
      addNotification(
        MESSAGEBAR_TYPE.ERROR,
        "Failed to update Additional Delay settings"
      );
    }
  }, [isUpdateSuccess, isUpdateError]);

  const onSaveHandler = () => {
    if (isUpdating) {
      return;
    }
    const payload = data[0];
    payload.greenToAmber = thresholdValues.greenToAmber;
    payload.amberToRed = thresholdValues.amberToRed;
    update([payload]);
  };

  useEffect(() => {
    if (refreshData) {
      onSaveHandler();
      setRefreshData(false);
    }
  }, [refreshData]);

  const greenToAmberThresholdHandler = (
    _e: React.ChangeEvent<HTMLInputElement>,
    target: any
  ) => {
    const value = parseInt(target.value);
    if (value > 0 && value < thresholdValues.amberToRed) {
      setThresholdValues((oldValues) => ({
        ...oldValues,
        greenToAmber: value,
      }));
    }
  };

  const amberToRedThresholdHandler = (
    _e: React.ChangeEvent<HTMLInputElement>,
    target: any
  ) => {
    const value = parseInt(target.value);
    if (value > thresholdValues.greenToAmber) {
      setThresholdValues((oldValues) => ({
        ...oldValues,
        amberToRed: value,
      }));
    }
  };

  return (
    <section className="pt-sm-3" data-testid="additional-delay-settings">
      <Divider className="mb-sm-4" />

      <h4 className="fs-20 fw-400 fc--gray900">Additional Delay Thresholds</h4>

      {isLoading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={classes.containerHeight}
        />
      )}
      {isError && (
        <div style={{ margin: "auto" }} className={classes.containerHeight}>
          <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />
        </div>
      )}

      {!isLoading && !isError && (
        <div
          className={classes.listContainer}
          aria-label="Additional Delay Settings"
        >
          <div className="amd-container-vcenter mb-1">
            <span className="d-flex jc-center">
              <AmdGreen />
            </span>
            <span className="ml-1">Good :</span>
            <span className="ml-1">Less than </span>
            <span className="ml-1">
              <Input
                type="number"
                className={classes.customInput}
                value={thresholdValues.greenToAmber?.toString()}
                aria-label="inline"
                onChange={greenToAmberThresholdHandler}
              />
            </span>
            <span className="ml-1">min</span>
          </div>

          <div className="amd-container-vcenter mb-1">
            <span className="d-flex jc-center">
              <AmdAmber />
            </span>
            <span className="mx-1 ">Moderate :</span>
            <Input
              type="number"
              value={thresholdValues.greenToAmber?.toString()}
              className={classes.customInput}
              aria-label="inline"
              disabled
            />
            <span className="mx-1">to less than </span>
            <Input
              className={classes.customInput}
              type="number"
              value={thresholdValues.amberToRed?.toString()}
              aria-label="inline"
              onChange={amberToRedThresholdHandler}
            />
            <span className="ml-1">min</span>
          </div>

          <div className="amd-container-vcenter mb-1">
            <span className="d-flex jc-center">
              <AmdRed />
            </span>
            <span className="ml-1">Poor :</span>
            <span className="mx-1">Greater than equal to</span>
            <Input
              type="number"
              className={classes.customInput}
              value={thresholdValues.amberToRed?.toString()}
              aria-label="inline"
              disabled
            />
            <span className="ml-1">min</span>
          </div>
        </div>
      )}
    </section>
  );
};

export default AdditionalDelaySettings;

const useStyles = makeStyles({
  customInput: {
    width: "45px",
    paddingLeft: "5px",
    "& input": {
      padding: "0",
    },
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    paddingBottom: "20px",
  },
  containerHeight: {
    minHeight: "160px",
  },
});
